// frontend/admin-portal/src/pages/Invoices.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Button,
  Table,
  Badge,
  Alert,
  formatMoney,
  usePersistedState,
} from "@fiberhome/shared-ui";
import { invoiceService } from "../services/invoiceService";
import InvoiceStats from "../components/sales/InvoiceStats";
import InvoiceModal from "../components/sales/InvoiceModal";

const STATUS_VARIANT = {
  draft: "default",
  issued: "primary",
  partial: "warning",
  paid: "success",
  overdue: "danger",
  void: "default",
};

export default function Invoices() {
  const navigate = useNavigate();
  const [view, setView] = usePersistedState("fiberhome.invoices.view", "list");
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    status: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await invoiceService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Sales invoices, receipts and outstanding balances"
        breadcrumbs={["Home", "Sales", "Invoices"]}
        actions={
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            + New Invoice
          </Button>
        }
      />

      <InvoiceStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="fh-table-toolbar mb-3">
        <input
          className="fh-input"
          placeholder="Search by code…"
          value={filters.search}
          onChange={(e) =>
            setFilters({ ...filters, search: e.target.value, page: 1 })
          }
        />
        <select
          className="fh-input"
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value, page: 1 })
          }
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="void">Void</option>
        </select>
      </div>

      <Table
        columns={[
          {
            key: "code",
            label: "Invoice",
            render: (r) => (
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                {r.code}
              </span>
            ),
          },
          {
            key: "customer",
            label: "Customer",
            render: (r) => r.customer?.name || "—",
          },
          {
            key: "issueDate",
            label: "Issued",
            render: (r) => new Date(r.issueDate).toLocaleDateString(),
          },
          {
            key: "dueDate",
            label: "Due",
            render: (r) =>
              r.dueDate ? new Date(r.dueDate).toLocaleDateString() : "—",
          },
          {
            key: "total",
            label: "Total",
            render: (r) => (
              <span style={{ fontWeight: 600 }}>
                {formatMoney(r.total, { symbol: r.currency })}
              </span>
            ),
          },
          {
            key: "paidAmount",
            label: "Paid",
            render: (r) => formatMoney(r.paidAmount, { symbol: r.currency }),
          },
          {
            key: "status",
            label: "Status",
            render: (r) => (
              <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            width: 120,
            render: (r) => (
              <Button
                size="sm"
                variant="primary"
                onClick={() => navigate(`/invoices/${r.id}`)}
              >
                Open
              </Button>
            ),
          },
        ]}
        data={items}
        loading={loading}
        emptyMessage="No invoices yet"
        rowKey="id"
      />

      <InvoiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
}
